import { Component, OnDestroy, Input, Output, ViewChild, EventEmitter, ContentChildren, QueryList, ElementRef, Renderer2 } from '@angular/core';
import { DragDrop, DropListRef, DragRef, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { merge, takeUntil, startWith, map, pairwise } from 'rxjs/operators';

import { contentExpansion } from './collapse-panel-animations';
import { CollapseItemComponent } from '../collapse-item/collapse-item.component';
import { DragDropRegistryService } from '../../../drag-drop-registry.service';

const REGISTRY_KEY = 'collapse-panels';

@Component({
  selector: 'app-collapse-panel',
  templateUrl: './collapse-panel.component.html',
  styleUrls: ['./collapse-panel.component.scss'],
  animations: [contentExpansion]
})
export class CollapsePanelComponent<T = any> implements OnDestroy {
  @Input() title: string;
  @Input() opened = false;
  @Input() dropListData: T[];
  @Output() dropListDropped: EventEmitter<any> = new EventEmitter();
  @Output() toggle = new Subject();

  @ViewChild('body', { static: false }) body: ElementRef;
  @ViewChild('header', { static: false }) header: ElementRef;

  @ContentChildren(CollapseItemComponent) collapseItems: QueryList<CollapseItemComponent>;
  private bodyDropList: DropListRef<T[]>;
  private headerDropList: DropListRef<T[]>;
  private dragItems: DragRef<T>[] = [];
  private lastDroppedIndex: number;
  private lastExitedIndex: number;
  private destroyed$ = new Subject();

  constructor(private renderer: Renderer2, private dragDrop: DragDrop, private dragDropRegistryService: DragDropRegistryService) { }

  ngAfterViewInit() {
    this.setDragItems();
    this.listenToDragEvents();
  }

  private setDragItems(): void {
    this.collapseItems.toArray().forEach(item => {
      this.dragItems.push(this.dragDrop.createDrag(item.elRef));
    });
  }

  private listenToDragEvents(): void {
    this.bodyDropList = this.dragDrop.createDropList(this.body);
    this.headerDropList = this.dragDrop.createDropList(this.header);

    this.dragDropRegistryService.add(REGISTRY_KEY, this.bodyDropList);
    this.dragDropRegistryService.add(REGISTRY_KEY, this.headerDropList);

    this.bodyDropList.data = this.dropListData;
    this.headerDropList.data = this.dropListData;

    this.headerDropList.enterPredicate = this.preventBodyItemEntrance.bind(this);

    this.bodyDropList.withItems(this.dragItems);
    this.headerDropList.withItems([]);

    this.bodyDropList.exited
      .pipe(takeUntil(this.destroyed$))
      .subscribe(event => this.onBodyExited(event));

    this.bodyDropList.dropped.pipe(
      merge(this.headerDropList.dropped),
      takeUntil(this.destroyed$)
    ).subscribe(event => this.onDropped(event));

    this.bodyDropList.beforeStarted
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.onBodyBeforeStarted())

    this.headerDropList.entered
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.onHeaderEntered());

    this.headerDropList.exited
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.onHeaderExited());

    this.collapseItems.changes.pipe(
      takeUntil(this.destroyed$),
      startWith(this.collapseItems),
      map((change: QueryList<any>) => change.length),
      pairwise()
    ).subscribe(lengths => this.onCollapseItemsChange(...lengths));
  }

  private onDropped(event): void {
    this.lastDroppedIndex = event.currentIndex;
    this.dropListDropped.emit(event);

    if (event.previousContainer === event.container) {
      moveItemInArray(this.dragItems, event.previousIndex, event.currentIndex);

      // https://github.com/angular/material2/pull/15103
      // Call withItems to keep everything in sync when an item is dropped.
      this.bodyDropList.withItems(this.dragItems);
    }

    this.renderer.removeClass(this.header.nativeElement, 'entered');
    this.renderer.removeClass(this.body.nativeElement, 'cdk-drop-list-dragging');
  }

  private onBodyBeforeStarted(): void {
    this.renderer.addClass(this.body.nativeElement, 'cdk-drop-list-dragging');
  }

  private onBodyExited(event: { item: DragRef, container: DropListRef<T> }): void {
    this.lastExitedIndex = this.dragItems.findIndex(item => item === event.item);
    this.renderer.removeClass(this.body.nativeElement, 'cdk-drop-list-dragging');
  }

  private onCollapseItemsChange(previousLength, currentLength): void {
    if (previousLength < currentLength) {
      const dragItem = this.dragDrop.createDrag(this.collapseItems.toArray()[this.lastDroppedIndex].elRef);
      this.dragItems.splice(this.lastDroppedIndex, 0, dragItem);

      // https://github.com/angular/material2/pull/15103
      // Call withItems to keep everything in sync when an item is dropped.
      this.bodyDropList.withItems(this.dragItems);
    }
    else if (previousLength > currentLength) {
      this.dragItems[this.lastExitedIndex].dispose();
      this.dragItems.splice(this.lastExitedIndex, 1);

      // https://github.com/angular/material2/pull/15103
      // Call withItems to keep everything in sync when an item is dropped.
      this.bodyDropList.withItems(this.dragItems);
    }
  }

  private onHeaderEntered(): void {
    this.renderer.addClass(this.header.nativeElement, 'entered');
  }

  private onHeaderExited(): void {
    this.renderer.removeClass(this.header.nativeElement, 'entered');
  }

  private preventBodyItemEntrance(dragItem: DragRef): boolean {
    return this.bodyDropList.getItemIndex(dragItem) === -1;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.dragDropRegistryService.remove(REGISTRY_KEY, this.bodyDropList);
    this.dragDropRegistryService.remove(REGISTRY_KEY, this.headerDropList);
  }
}
